import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { Suspense, type ReactNode } from 'react';
import { Outlet } from 'react-router';

import { CoreText } from '../../base-ui/CoreText';
import authBackgroundPath from '../../base-ui/graphics/auth/AuthBackground.svg?url';
import decorationIllustrationPath from '../../base-ui/graphics/auth/DecorationIllustration.svg?url';
import { FullLogo } from '../../base-ui/graphics/FullLogo';
import { theme } from '../../theme/theme';

const fadeIn = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const PageContainer = styled.div`
	inline-size: 100%;
	block-size: 100%;
	overflow: hidden;

	background: url('${authBackgroundPath}') no-repeat center center fixed;
	background-size: cover;

	display: flex;
	align-items: center;
	justify-content: center;
`;

const ContentContainer = styled.div`
	overflow: hidden;
	background: rgba(255, 255, 255, 0.7);
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
	border-radius: 8px;

	inline-size: 800px;
	max-inline-size: 90%;

	@media (width >= 601px) {
		min-block-size: 500px;
	}

	block-size: 500px;

	display: flex;
	flex-wrap: nowrap;

	animation: ${fadeIn} 0.3s ease-in;

	& > * {
		flex: 1 1 0;
		display: flex;
		block-size: 100%;
	}

	flex-direction: row;

	@media (width <= 768px) {
		flex-direction: column-reverse;
	}
`;

const FormBlock = styled.div`
	text-align: center;
	inline-size: 100%;
	padding: 20px;
	word-break: break-word;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow-x: hidden;

	> * {
		max-inline-size: 100%;
	}

	flex-direction: column;
`;

const DecorationBlock = styled.div`
	padding: 0;
	background: ${theme.colors.primary};
	background-image: url('${decorationIllustrationPath}');
	background-repeat: no-repeat;
	background-size: cover;

	@media (width <= 768px) {
		max-block-size: 15%;
	}
`;

const ContentBlock = styled.div`
	position: relative;
`;

const ContactUs = styled.div`
	position: absolute;
	inset-block-end: 0;
	inline-size: 100%;
	text-align: center;
	margin-block-end: 30px;
`;

const LogoWrapper = styled.div`
	margin-block-end: 30px;
`;

interface AuthLayoutContainerProps {
	readonly children: ReactNode;
}

export const AuthLayoutContainer = observer(function AuthLayout({ children }: AuthLayoutContainerProps) {
	return (
		<PageContainer>
			<ContentContainer>
				<ContentBlock>
					<FormBlock>
						<LogoWrapper>
							<FullLogo height={28} />
						</LogoWrapper>
						<Suspense fallback={<div />}>{children}</Suspense>
					</FormBlock>
					{localStorage.getItem('ssoUrl') && (
						<ContactUs>
							<CoreText type='body2'>
								Don't have an account?{' '}
								<CoreText type='body2' color='accentGreen' cursor='pointer'>
									<a href='mailto:support@navina.ai' target='_blank' rel='noopener noreferrer'>
										Contact support
									</a>
								</CoreText>
							</CoreText>
						</ContactUs>
					)}
				</ContentBlock>
				<DecorationBlock />
			</ContentContainer>
		</PageContainer>
	);
});

export const AuthLayout = observer(function AuthLayout() {
	return (
		<AuthLayoutContainer>
			<Outlet />
		</AuthLayoutContainer>
	);
});
